import { useState } from "react";
import { AlertTriangle, Plus, Wand2, X } from "lucide-react";
import { toast } from "sonner";

import { BottomSheet, ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import { fmtSlot, gameLabel, inputStyle, pillBtn, teamName } from "./shared";

export default function ScheduleTab({ data, isDirector, onChanged }) {
  const t = data.tournament;
  const [divFilter, setDivFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const teamsById = Object.fromEntries(data.teams.map((x) => [x.id, x]));
  const divName = Object.fromEntries(data.divisions.map((d) => [d.id, d.name]));
  const multi = data.divisions.length > 1;
  const games = data.games.filter((g) => !g.is_bye && (divFilter === "all" || g.division_id === divFilter));
  const placed = games.filter((g) => g.court_id && g.scheduled_at);
  const unplaced = games.filter((g) => !(g.court_id && g.scheduled_at));
  const times = Array.from(new Set(placed.map((g) => g.scheduled_at))).sort();
  const conflicts = data.schedule_conflicts || {};
  const conflictCount = Object.keys(conflicts).length;
  const card = (g) => (
    <ScheduleCard key={g.id} g={g} teamsById={teamsById} tz={t.timezone} conflicts={conflicts[g.id]}
                  label={`${multi ? `${divName[g.division_id]} · ` : ""}${gameLabel(g)}`} onClick={() => setEditing(g)} />
  );

  return (
    <div data-testid="schedule-tab">
      <Courts data={data} isDirector={isDirector} onChanged={onChanged} />
      <div style={{ display: "flex", gap: 6, alignItems: "center", margin: "10px 0", flexWrap: "wrap" }}>
        {multi && [{ id: "all", name: "All" }, ...data.divisions].map((d) => (
          <button key={d.id} data-testid={`schedule-div-${d.id}`} onClick={() => setDivFilter(d.id)}
                  style={pillBtn(divFilter === d.id, "var(--green-mid)")}>{d.name}</button>
        ))}
        {isDirector && (
          <button data-testid="schedule-autofill-open" onClick={() => setAutoOpen(true)}
                  style={{ ...pillBtn(true, "var(--chalk-board)"), marginLeft: "auto", display: "inline-flex", gap: 4, alignItems: "center" }}>
            <Wand2 size={13} aria-hidden="true" /> Auto-fill
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginBottom: 8 }}>
        Min rest between a team's games: {t.min_rest_min} min · Default game length: {t.default_duration_min} min
      </div>
      {conflictCount > 0 && (
        <div data-testid="schedule-conflict-banner" style={{ background: "var(--chalk-red-bg)", color: "var(--chalk-red)",
                                                             borderRadius: 10, padding: 10, fontSize: 12, fontWeight: 700, marginBottom: 10,
                                                             display: "flex", gap: 6, alignItems: "center" }}>
          <AlertTriangle size={14} aria-hidden="true" /> {conflictCount} game{conflictCount > 1 ? "s" : ""} with schedule conflicts (shown in red)
        </div>
      )}
      {data.courts.length > 0 && times.length > 0 && (
        <div style={{ overflowX: "auto", marginBottom: 12 }} data-testid="schedule-grid">
          <div style={{ display: "grid", gridTemplateColumns: `56px repeat(${data.courts.length}, minmax(150px, 1fr))`, gap: 6 }}>
            <div />
            {data.courts.map((c) => (
              <div key={c.id} style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{c.name}</div>
            ))}
            {times.map((iso) => (
              <Row key={iso} iso={iso} tz={t.timezone} courts={data.courts}
                   games={placed.filter((g) => g.scheduled_at === iso)} card={card} />
            ))}
          </div>
        </div>
      )}
      <SectionTitle label={`Unscheduled (${unplaced.length})`} />
      {unplaced.length === 0 && <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>Every game has a location and time.</div>}
      {unplaced.map(card)}
      {editing && (
        <ScheduleSheet data={data} game={editing} teamsById={teamsById}
                       onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await onChanged(); }} />
      )}
      {autoOpen && <AutofillSheet data={data} onClose={() => setAutoOpen(false)}
                                  onDone={async () => { setAutoOpen(false); await onChanged(); }} />}
    </div>
  );
}

function Row({ iso, tz, courts, games, card }) {
  return (
    <>
      <div style={{ fontSize: 11, fontWeight: 700, paddingTop: 10 }}>{fmtSlot(iso, tz, false)}</div>
      {courts.map((c) => (
        <div key={c.id}>{games.filter((g) => g.court_id === c.id).map(card)}</div>
      ))}
    </>
  );
}

function ScheduleCard({ g, teamsById, tz, conflicts, label, onClick }) {
  const bad = conflicts && conflicts.length > 0;
  return (
    <ChalkCard data-testid={`schedule-game-${g.id}`} onClick={onClick}
               style={{ marginBottom: 6, cursor: "pointer", padding: 10,
                        border: bad ? "1.5px solid var(--chalk-red)" : undefined,
                        background: bad ? "var(--chalk-red-bg)" : undefined }}>
      <div style={{ fontSize: 10, color: "var(--chalk-muted)", fontWeight: 700 }}>{label} · {g.status}</div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{teamName(teamsById, g.home_team_id)}</div>
      <div style={{ fontSize: 13 }}>{teamName(teamsById, g.away_team_id)}</div>
      {!g.court_id && g.scheduled_at && <div style={{ fontSize: 10, marginTop: 2 }}>{fmtSlot(g.scheduled_at, tz)} · no location</div>}
      {bad && (
        <div data-testid={`schedule-conflict-${g.id}`} style={{ fontSize: 10, color: "var(--chalk-red)", marginTop: 4, fontWeight: 700 }}>
          {conflicts.map((c) => c.message).join(" · ")}
        </div>
      )}
    </ChalkCard>
  );
}

function Courts({ data, isDirector, onChanged }) {
  const [name, setName] = useState("");
  const t = data.tournament;
  async function run(fn) {
    try { await fn(); await onChanged(); } catch (e) { toast.error(e.message || "Couldn't save."); }
  }
  return (
    <div data-testid="locations-bar">
      <SectionTitle label={`Locations (${data.courts.length})`} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {data.courts.map((c) => (
          <span key={c.id} data-testid={`location-chip-${c.id}`} style={{ ...pillBtn(false), display: "inline-flex", gap: 4, alignItems: "center" }}>
            {c.name}
            {isDirector && (
              <button aria-label={`Remove ${c.name}`} data-testid={`location-remove-${c.id}`}
                      onClick={() => window.confirm(`Remove ${c.name}? Games there become unscheduled.`) && run(() => tournaments.deleteCourt(t.id, c.id))}
                      style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, color: "var(--chalk-muted)" }}>
                <X size={12} aria-hidden="true" />
              </button>
            )}
          </span>
        ))}
      </div>
      {isDirector && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${t.rules?.location_noun || "Location"} 1`}
                 data-testid="location-name-input" style={{ ...inputStyle, flex: 1 }} />
          <button data-testid="location-add" aria-label="Add location"
                  onClick={() => name.trim() && run(() => tournaments.createCourt(t.id, name.trim())).then(() => setName(""))}
                  style={{ ...pillBtn(true, "var(--green-mid)"), display: "inline-flex", alignItems: "center" }}>
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

function ScheduleSheet({ data, game, teamsById, onClose, onSaved }) {
  const t = data.tournament;
  const [court, setCourt] = useState(game.court_id || data.courts[0]?.id || "");
  const [when, setWhen] = useState(game.scheduled_local || `${String(t.starts_on).slice(0, 10)}T09:00`);
  const [dur, setDur] = useState(game.duration_min || t.default_duration_min);
  const [conflicts, setConflicts] = useState(null);
  const [busy, setBusy] = useState(false);

  async function save(override = false, clear = false) {
    setBusy(true);
    try {
      await tournaments.scheduleGame(t.id, game.id, clear ? { expected_version: game.version } : {
        court_id: court || null, local_time: when || null, duration_min: Number(dur) || null,
        expected_version: game.version, override,
      });
      toast.success(clear ? "Slot cleared." : "Scheduled.");
      onSaved();
    } catch (e) {
      if (e.code === "SCHEDULE_CONFLICT") setConflicts(e.meta?.conflicts || []);
      else if (e.code === "GAME_CHANGED") toast.error("Someone else just changed this game. Close and reopen.");
      else toast.error(e.message || "Couldn't schedule.");
    } finally { setBusy(false); }
  }

  return (
    <BottomSheet isOpen title={`${gameLabel(game)} · ${teamName(teamsById, game.home_team_id)} vs ${teamName(teamsById, game.away_team_id)}`} onDismiss={onClose}>
      <div data-testid="schedule-sheet" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <label style={{ fontSize: 12, fontWeight: 700 }}>Location
          <select value={court} onChange={(e) => setCourt(e.target.value)} data-testid="schedule-location" style={{ ...inputStyle, marginTop: 4 }}>
            <option value="">No location</option>
            {data.courts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, fontWeight: 700 }}>Start ({t.timezone})
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                 data-testid="schedule-time" style={{ ...inputStyle, marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 12, fontWeight: 700 }}>Length (min)
          <input type="number" value={dur} onChange={(e) => setDur(e.target.value)} data-testid="schedule-duration"
                 style={{ ...inputStyle, marginTop: 4 }} />
        </label>
        {conflicts && (
          <div data-testid="schedule-sheet-conflicts" style={{ background: "var(--chalk-red-bg)", color: "var(--chalk-red)", borderRadius: 10, padding: 10, fontSize: 12 }}>
            <b>Conflicts:</b>
            <ul style={{ margin: "4px 0 0 16px" }}>{conflicts.map((c, i) => <li key={i}>{c.message}</li>)}</ul>
          </div>
        )}
        {conflicts ? (
          <ChalkActionButton variant="danger" label="Save anyway" loading={busy} testId="schedule-override" onClick={() => save(true)} />
        ) : (
          <ChalkActionButton variant="primary" label="Save slot" loading={busy} testId="schedule-save" onClick={() => save(false)} />
        )}
        {game.scheduled_at && (
          <ChalkActionButton variant="ghost" label="Clear slot" testId="schedule-clear" onClick={() => save(true, true)} />
        )}
      </div>
    </BottomSheet>
  );
}

function AutofillSheet({ data, onClose, onDone }) {
  const t = data.tournament;
  const [start, setStart] = useState(`${String(t.starts_on).slice(0, 10)}T08:00`);
  const [dur, setDur] = useState(t.default_duration_min);
  const [every, setEvery] = useState(t.default_duration_min);
  const [rest, setRest] = useState(t.min_rest_min);
  const [division, setDivision] = useState("all");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      const d = await tournaments.autofill(t.id, {
        start_local: start, interval_min: Number(every), duration_min: Number(dur),
        min_rest_min: Number(rest), overwrite,
        division_ids: division === "all" ? null : [division],
      });
      toast.success(`Scheduled ${d.placed} game(s).${d.unplaced.length ? ` ${d.unplaced.length} couldn't fit.` : ""}`);
      onDone();
    } catch (e) { toast.error(e.message || "Auto-fill failed."); }
    finally { setBusy(false); }
  }
  const field = (label, val, set, testId, type = "number") => (
    <label style={{ fontSize: 12, fontWeight: 700 }}>{label}
      <input type={type} value={val} onChange={(e) => set(e.target.value)} data-testid={testId} style={{ ...inputStyle, marginTop: 4 }} />
    </label>
  );
  return (
    <BottomSheet isOpen title="Auto-fill schedule" onDismiss={onClose}>
      <div data-testid="autofill-sheet" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", lineHeight: 1.5 }}>
          Spreads games across all locations in round order. Never double-books a team or location, never starts a game
          before its feeder games can finish, and keeps the minimum rest gap.
        </div>
        {field(`First game (${t.timezone})`, start, setStart, "autofill-start", "datetime-local")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {field("Every (min)", every, setEvery, "autofill-interval")}
          {field("Length (min)", dur, setDur, "autofill-duration")}
          {field("Min rest", rest, setRest, "autofill-rest")}
        </div>
        {data.divisions.length > 1 && (
          <select value={division} onChange={(e) => setDivision(e.target.value)} data-testid="autofill-division" style={inputStyle}>
            <option value="all">All divisions</option>
            {data.divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="autofill-overwrite" />
          Re-schedule games that already have a slot
        </label>
        <ChalkActionButton variant="primary" label="Auto-fill" loading={busy} testId="autofill-run" onClick={go} />
      </div>
    </BottomSheet>
  );
}
